<script setup>
import { reactive, ref, watch, computed } from 'vue'
import appAxios from '@/libs/app-axios'
import { ErrorPopup, LoadingPopup } from '@/libs/utils'

const emit = defineEmits(['savedAttendance'])

const isSaving = ref(false)

const shifts = [
  { text: 'Day Shift (9:00 AM - 6:00 PM)', value: 'day' },
  { text: 'Night Shift (2:00 PM - 10:00 PM)', value: 'night' },
]

const formState = reactive({
  date: '',
  shift: '', // UI only (not sent to backend)
  time_in: '',
  time_out: '',
  ot_in: '',
  ot_out: '',
  ot_reason: '',
  has_meal_allowance: false,
})

const getAttendanceSheetForDate = async (date) => {
  try {
    if (!date) return
    LoadingPopup.fire({
      title: 'Checking if attendance sheet exists for the selected date...',
    })

    const { data } = await appAxios().get(`/v3/attendance-sheets/${date}/get-by-date`)

    formState.time_in = data?.time_in || ''
    formState.time_out = data?.time_out || ''
    formState.ot_in = data?.ot_in || ''
    formState.ot_out = data?.ot_out || ''
    formState.ot_reason = data?.ot_reason || ''
    formState.has_meal_allowance = data?.has_meal_allowance || false

    LoadingPopup.close()
  } catch (e) {
    LoadingPopup.close()
    console.error(e)
    ErrorPopup.fire({
      title: 'Failed to fetch attendance sheet for the selected date',
      text: 'Please try again later.',
    })
  }
}

/* Autofill time based on shift */
watch(
  () => formState.shift,
  (val) => {
    if (val === 'day') {
      formState.time_in = '09:00:00'
      formState.time_out = '18:00:00'
      return
    }
    if (val === 'night') {
      formState.time_in = '14:00:00'
      formState.time_out = '22:00:00'
      return
    }
  }
)

watch(
  () => formState.date,
  (val) => {
    getAttendanceSheetForDate(val)
  }
)

const saveAttendance = async (payload) => {
  await appAxios().post('/v3/attendance-sheets', payload)
  emit('savedAttendance')
}

const submit = async () => {
  if (!formState.date) {
    alert('Date is required')
    return
  }

  try {
    isSaving.value = true

    // remove shift before sending
    const { shift, ...payload } = formState

    await saveAttendance(payload)

    formState.date = ''
    formState.shift = ''
    formState.time_in = ''
    formState.time_out = ''
    formState.ot_in = ''
    formState.ot_out = ''
    formState.ot_reason = ''
    formState.has_meal_allowance = false
  } finally {
    isSaving.value = false
  }
}

const today = computed(() => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}` // YYYY-MM-DD (local time)
})
</script>

<template>
  <v-card outlined elevation="1" class="pa-4">
    <v-row dense>
      <v-col cols="12" md="3">
        <v-text-field hide-details="auto" v-model="formState.date" type="date" label="Date" outlined dense
          :max="today" />
      </v-col>
      <v-col cols="12" md="3">
        <v-select v-model="formState.shift" :items="shifts" label="Shift" outlined dense hide-details="auto" />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field hide-details="auto" v-model="formState.time_in" type="time" step="1" label="Time In" outlined
          dense />
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field hide-details="auto" v-model="formState.time_out" type="time" step="1" label="Time Out" outlined
          dense />
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field hide-details="auto" v-model="formState.ot_in" type="time" step="1" label="OT In" outlined dense />
      </v-col>

      <v-col cols="12" md="2">
        <v-text-field hide-details="auto" v-model="formState.ot_out" type="time" step="1" label="OT Out" outlined
          dense />
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field hide-details="auto" v-model="formState.ot_reason" label="OT Reason" outlined dense />
      </v-col>

      <v-col cols="12" md="3" class="d-flex align-center">
        <v-switch style="margin-top: 0;" dense v-model="formState.has_meal_allowance" label="Meal Allowance"
          hide-details />
      </v-col>

      <v-col cols="12" md="2">
        <v-btn color="primary" block :loading="isSaving" @click="submit">
          Save
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'AttendanceSheetForm',
}
</script>
